/**
 * "Add Documents", on the same upload dialog as case files: a drop surface
 * (files, folders, SharePoint, Google Drive or a case's draft), a rail of the
 * files about to be added, a preview, an optional "Set for Every File" (kind,
 * visas, tags), and one button. Files (or a .zip, unpacked on the server) go
 * straight to storage, then up as one batch; files over the size limit are
 * refused before anything is sent. The agent reads each one and writes its
 * name, description and guidance; the rows appear in the library at once and
 * go live on their own. A file whose exact bytes are already in the library
 * is held back unless the user adds it anyway.
 *
 * Add To picks the destination folder (the one open in the Library by
 * default). A dropped Finder folder or a .zip keeps its tree there, merged by
 * name into folders that already exist, unless Keep Folder Structure is off;
 * the line under it says what will be created.
 */
import {
  Briefcase,
  ChevronDown,
  ChevronRight,
  Folder,
  Info,
  Layers
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC
} from "react";
import { toast } from "sonner";

import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { UploadDialogShell } from "@/components/upload/UploadDialogShell";
import {
  UploadFileCard,
  type UploadStatus
} from "@/components/upload/UploadFileCard";
import {
  UploadSourceButton,
  UploadSurface
} from "@/components/upload/UploadSurface";

import {
  withoutSystemFiles,
  type CollectedFile
} from "../../caseFiles/upload/collectDroppedFiles";
import { StagedFilePreview } from "../../caseIde/files/StagedFilePreview";
import {
  folderPartsOf,
  folderPathLabel,
  folderTree,
  planUpload,
  type FolderTree
} from "../folders";
import {
  ACCEPTED_EXTENSIONS,
  CATEGORIES,
  CATEGORY_LABEL,
  duplicateOf,
  formatBytes,
  rejectedFiles,
  rejectionOf,
  sha256Hex
} from "../model";
import type {
  FirmLibraryBatch,
  FirmLibraryCategory,
  FirmLibraryEntry,
  FirmLibraryFolder,
  UploadPresets
} from "../types";
import { AddFromCaseDialog } from "./AddFromCaseDialog";
import { FolderSelect, type FolderSelectChoice } from "./FolderSelect";
import { TagInput } from "./TagInput";
import { VisaPicker } from "./VisaPicker";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: FirmScope;
  /** The library as loaded, for the duplicate check. */
  entries: FirmLibraryEntry[];
  /** The firm's folders, for Add To. */
  folders?: FirmLibraryFolder[];
  /** Add To's starting point: the folder open in the Library. */
  defaultFolderId?: string | null;
  /** Files dropped on the page before the dialog opened. */
  initialCollected?: CollectedFile[] | null;
  /** A batch was queued; the list shows its progress. */
  onBatchStarted: (batch: FirmLibraryBatch) => void;
  onOpenEntry?: (entryId: string) => void;
};

const TOP_LEVEL = "__top__";
const DESTINATION_CHOICES: FolderSelectChoice[] = [
  { value: TOP_LEVEL, label: "Top Level", icon: Layers }
];

type Picked = {
  key: string;
  file: File;
  /** Where it sat in the drop (`"Harbor/Org Charts/chart.pdf"`); the name for a loose file. */
  relativePath: string;
  /** The duplicate check is still hashing the file. */
  checking: boolean;
  /** The library entry with the same bytes, once the checksum is in. */
  duplicate: { id: string; name: string } | null;
  /** False for a duplicate until the user adds it anyway. */
  include: boolean;
};

const AUTO = "__auto__";

const HINT =
  ".docx, .pdf, .doc, .rtf, .odt, .pptx, .xlsx or a .zip of them, up to 100 MB each";

const keyOf = ({ file, relativePath }: CollectedFile) =>
  `${relativePath || file.name}:${file.size}`;

const plural = (n: number, one: string) => `${n} ${n === 1 ? one : `${one}s`}`;

const isZip = (file: File) => /\.zip$/i.test(file.name);

/** Picked files by the folder they sat in, loose files first. */
const folderGroups = <T extends { relativePath: string }>(
  items: T[]
): [string, T[]][] => {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const path = folderPartsOf(item.relativePath).join("/");
    groups.set(path, [...(groups.get(path) ?? []), item]);
  }
  return [...groups].sort(([a], [b]) =>
    a === "" ? -1 : b === "" ? 1 : a.localeCompare(b)
  );
};

/** What Add To and Keep Folder Structure will do, in one sentence. */
const resultLine = (
  tree: FolderTree,
  destination: string | null,
  files: { relativePath: string; file: File }[],
  keep: boolean
): string => {
  const where = destination
    ? folderPathLabel(tree, destination)
    : "the top level";
  const count = plural(files.length, "document");
  const verb = files.length === 1 ? "goes" : "go";
  const zips = files.some((p) => isZip(p.file));
  if (!keep)
    return `${count} ${verb} straight into ${where}. No folders are created.`;
  const plan = planUpload(
    tree,
    destination,
    files.map((p) => p.relativePath)
  );
  const parts = [`${count} ${verb} into ${where}.`];
  if (plan.merged.length)
    parts.push(
      `${plan.merged.join(", ")} already ${
        plan.merged.length === 1 ? "exists" : "exist"
      } there, so ${plan.merged.length === 1 ? "it merges" : "they merge"}.`
    );
  if (plan.created)
    parts.push(`Creates ${plural(plan.created, "folder")} from the drop.`);
  if (zips) parts.push("A .zip keeps its folders too.");
  if (plan.flattened)
    parts.push("Anything deeper than four levels flattens into the fourth.");
  return parts.join(" ");
};

const checksumOf = async (file: File): Promise<string | null> => {
  if (!globalThis.crypto?.subtle || typeof file.arrayBuffer !== "function")
    return null;
  try {
    return await sha256Hex(await file.arrayBuffer());
  } catch {
    return null;
  }
};

const presetSummary = (presets: UploadPresets): string | null => {
  const parts = [
    presets.category ? CATEGORY_LABEL[presets.category] : null,
    presets.visas?.length ? presets.visas.join(", ") : null,
    presets.tags?.length ? presets.tags.map((t) => `#${t}`).join(" ") : null
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
};

export const UploadDialog: FC<Props> = ({
  open,
  onOpenChange,
  scope,
  entries,
  folders,
  defaultFolderId = null,
  initialCollected,
  onBatchStarted,
  onOpenEntry
}) => {
  const [picked, setPicked] = useState<Picked[]>([]);
  const [progress, setProgress] = useState<number | null>(null);
  /** Each file's share sent, by `Picked.key`, while uploading. */
  const [sent, setSent] = useState<Record<string, number>>({});
  const [presets, setPresets] = useState<UploadPresets>({});
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [fromCaseOpen, setFromCaseOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [destination, setDestination] = useState<string | null>(
    defaultFolderId
  );
  const [keepStructure, setKeepStructure] = useState(true);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const lastSeeded = useRef<CollectedFile[] | null>(null);
  const busy = progress !== null;
  const tree = useMemo(() => folderTree(folders ?? []), [folders]);

  // Each opening starts at the folder open in the Library.
  useEffect(() => {
    if (open) setDestination(defaultFolderId);
  }, [open, defaultFolderId]);

  const rejected = useMemo(
    () => new Set(rejectedFiles(picked.map((p) => p.file))),
    [picked]
  );
  const sending = useMemo(
    () => picked.filter((p) => p.include && !rejected.has(p.file)),
    [picked, rejected]
  );
  const held = picked.filter((p) => p.duplicate && !p.include).length;
  const checking = picked.some((p) => p.checking);
  const dropHasFolders = picked.some(
    (p) => folderPartsOf(p.relativePath).length > 0
  );
  const offersStructure = dropHasFolders || picked.some((p) => isZip(p.file));
  const grouped = keepStructure && dropHasFolders;

  const reset = useCallback(() => {
    setPicked([]);
    setProgress(null);
    setSent({});
    setPresets({});
    setPresetsOpen(false);
    setSelectedKey(null);
    setKeepStructure(true);
    lastSeeded.current = null;
  }, []);

  const close = useCallback(() => {
    if (busy) return;
    reset();
    onOpenChange(false);
  }, [busy, reset, onOpenChange]);

  const addFiles = useCallback((incoming: CollectedFile[]) => {
    if (!incoming.length) return;
    setPicked((current) => {
      const seen = new Set(current.map((p) => p.key));
      const fresh = incoming.filter((c) => {
        const key = keyOf(c);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return [
        ...current,
        ...fresh.map((c) => ({
          key: keyOf(c),
          file: c.file,
          relativePath: c.relativePath || c.file.name,
          checking: true,
          duplicate: null,
          include: true
        }))
      ];
    });
    incoming.forEach((collected) => {
      void checksumOf(collected.file).then((checksum) => {
        const match = checksum
          ? duplicateOf(checksum, entriesRef.current)
          : null;
        setPicked((current) =>
          current.map((p) =>
            p.key === keyOf(collected) && p.checking
              ? match
                ? {
                    ...p,
                    checking: false,
                    duplicate: { id: match.id, name: match.name },
                    include: false
                  }
                : { ...p, checking: false }
              : p
          )
        );
      });
    });
  }, []);

  const addCollected = useCallback(
    (collected: CollectedFile[]) => addFiles(withoutSystemFiles(collected)),
    [addFiles]
  );

  useEffect(() => {
    if (
      !open ||
      !initialCollected?.length ||
      lastSeeded.current === initialCollected
    )
      return;
    lastSeeded.current = initialCollected;
    addCollected(initialCollected);
  }, [open, initialCollected, addCollected]);

  const removeFile = (key: string) => {
    setSelectedKey((current) => (current === key ? null : current));
    setPicked((current) => current.filter((p) => p.key !== key));
  };

  const toggleInclude = (key: string) =>
    setPicked((current) =>
      current.map((p) => (p.key === key ? { ...p, include: !p.include } : p))
    );

  const fileProgress = (target: Picked): number | null => {
    if (progress === null || !sending.includes(target)) return null;
    return sent[target.key] ?? 0;
  };

  const submit = async () => {
    if (sending.length === 0) return;
    setSelectedKey(null);
    setProgress(0);
    setSent({});
    try {
      const { batch, failed } = await firmKnowledgeApi.createBatch(
        sending.map((p) => p.file),
        scope,
        {
          presets,
          onProgress: setProgress,
          onFileProgress: (index, fraction) => {
            const key = sending[index]?.key;
            if (key) setSent((current) => ({ ...current, [key]: fraction }));
          },
          folderId: destination,
          relativePaths:
            keepStructure && dropHasFolders
              ? sending.map((p) => p.relativePath)
              : undefined,
          keepStructure
        }
      );
      const added = sending.length - failed.length;
      toast.success(
        added === 1
          ? "Adding 1 document. The agent is reading it."
          : `Adding ${added} documents. The agent is reading them.`
      );
      if (failed.length)
        toast.warning(
          `${
            failed.length === 1 ? "1 file" : `${failed.length} files`
          } could not be uploaded: ${failed.join(", ")}.`
        );
      onBatchStarted(batch);
      reset();
      onOpenChange(false);
    } catch (error) {
      setProgress(null);
      toast.error(error instanceof Error ? error.message : "Upload failed.");
    }
  };

  const openEntry = (id: string) => {
    if (!onOpenEntry || busy) return;
    close();
    onOpenEntry(id);
  };

  const selected = picked.find((p) => p.key === selectedKey) ?? null;
  const unsupported = picked.filter((p) => rejected.has(p.file)).length;
  const summary = picked.length
    ? [
        `${sending.length} ready`,
        held ? `${held} already in the library` : null,
        unsupported ? `${unsupported} can't be added` : null
      ]
        .filter(Boolean)
        .join(" · ")
    : null;
  const presetLine = presetSummary(presets);

  const card = (p: Picked) => {
    const bad = rejected.has(p.file);
    const fraction = p.include && !bad ? fileProgress(p) : null;
    const status: UploadStatus = bad
      ? "muted"
      : p.checking || (fraction !== null && fraction < 1)
      ? "busy"
      : p.duplicate && !p.include
      ? "warn"
      : "ready";
    const size = formatBytes(p.file.size);
    const subtitle = bad
      ? rejectionOf(p.file) ?? "Not a supported format"
      : fraction !== null
      ? fraction >= 1
        ? "Sent"
        : `Uploading… ${Math.round(fraction * 100)}%`
      : p.checking
      ? "Checking…"
      : p.duplicate
      ? `${p.include ? "Adding again" : "Held back"} · ${size}`
      : `Ready · ${size}`;
    return (
      <UploadFileCard
        key={p.key}
        id={p.key}
        name={p.file.name}
        state={
          bad
            ? "unsupported"
            : p.checking
            ? "checking"
            : p.duplicate
            ? "duplicate"
            : "ready"
        }
        status={status}
        subtitle={subtitle}
        subtitleTone={p.duplicate && !p.include && !bad ? "warn" : "muted"}
        excluded={bad}
        selected={p.key === selectedKey}
        onSelect={bad ? undefined : () => setSelectedKey(p.key)}
        onRemove={busy ? undefined : () => removeFile(p.key)}
        testId="upload-file"
      >
        {p.duplicate && !bad ? (
          <div className="relative z-10 mt-1.5 space-y-1.5 pl-16">
            <p
              className="line-clamp-2 text-sm text-fg-2"
              data-testid="upload-duplicate"
            >
              Already in the library as{" "}
              <span className="font-medium text-fg">{p.duplicate.name}</span>
            </p>
            {!busy ? (
              <div className="flex items-center gap-1.5">
                <Button
                  variant={p.include ? "ghost" : "secondary"}
                  size="xs"
                  onClick={() => toggleInclude(p.key)}
                  data-testid="upload-include-duplicate"
                >
                  {p.include ? "Skip" : "Add Anyway"}
                </Button>
                {onOpenEntry ? (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => p.duplicate && openEntry(p.duplicate.id)}
                  >
                    Open
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {fraction !== null && fraction < 1 ? (
          <Progress value={fraction * 100} className="mt-2 h-1" />
        ) : null}
      </UploadFileCard>
    );
  };

  const rail = picked.length ? (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex items-center justify-between gap-2 rounded-xl bg-elevated px-3 py-2 shadow-sm ring-1 ring-line-2">
        <span className="text-sm font-medium text-fg-3">Ready to Add</span>
        <span className="text-sm text-fg-3">
          {sending.length} {sending.length === 1 ? "file" : "files"}
        </span>
      </div>
      <ul
        className="no-scrollbar flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-1 py-1"
        data-testid="upload-file-list"
      >
        {grouped
          ? folderGroups(picked).map(([path, group]) => (
              <li key={path || "__loose__"} className="flex flex-col gap-2">
                {path ? (
                  <div
                    className="flex items-center gap-1.5 px-1 pt-1 text-sm text-fg-3"
                    data-testid="upload-folder-group"
                  >
                    <Folder className="size-3.5" aria-hidden />
                    <span className="min-w-0 truncate font-medium text-fg-2">
                      {path.split("/").join(" / ")}
                    </span>
                    <span className="shrink-0 tabular-nums">
                      · {plural(group.length, "file")}
                    </span>
                  </div>
                ) : null}
                <ul className="flex flex-col gap-2">{group.map(card)}</ul>
              </li>
            ))
          : picked.map(card)}
      </ul>
    </div>
  ) : null;

  // A firm with no folders, dropping loose files, sees today's dialog.
  const destinationSection = !(folders?.length || offersStructure) ? null : (
    <div
      className="divide-y divide-line-1 rounded-xl bg-elevated ring-1 ring-line-2"
      data-testid="upload-destination"
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        <Label className="w-16 shrink-0">Add To</Label>
        <FolderSelect
          tree={tree}
          value={destination ?? TOP_LEVEL}
          onChange={(value) =>
            setDestination(value === TOP_LEVEL ? null : value)
          }
          choices={DESTINATION_CHOICES}
          ariaLabel="Add To"
          className="flex-1"
          disabled={busy}
          data-testid="upload-add-to"
        />
      </div>
      {offersStructure ? (
        <div className="flex items-center gap-3 px-3 py-2.5">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <Label htmlFor="upload-keep-structure">Keep Folder Structure</Label>
            <span className="text-sm text-fg-3">
              Subfolders in the drop become folders in the Library.
            </span>
          </div>
          <Switch
            id="upload-keep-structure"
            checked={keepStructure}
            onCheckedChange={setKeepStructure}
            disabled={busy}
            data-testid="upload-keep-structure"
          />
        </div>
      ) : null}
      {sending.length && (offersStructure || destination) ? (
        <div
          className="flex items-start gap-2 rounded-b-xl bg-fill-1 px-3 py-2.5"
          data-testid="upload-result"
        >
          <Info className="mt-0.5 size-3.5 shrink-0 text-fg-3" aria-hidden />
          <span className="text-sm text-fg-2">
            {resultLine(tree, destination, sending, keepStructure)}
          </span>
        </div>
      ) : null}
    </div>
  );

  const presetsSection = (
    <div
      className="rounded-xl bg-elevated ring-1 ring-line-2"
      data-testid="upload-presets"
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-base hover:bg-fill-1 focus-visible:ring-2 focus-visible:ring-accent-line focus-visible:outline-none disabled:pointer-events-none disabled:opacity-60"
        aria-expanded={presetsOpen}
        onClick={() => setPresetsOpen((v) => !v)}
        disabled={busy}
      >
        {presetsOpen ? (
          <ChevronDown className="size-4 text-fg-3" />
        ) : (
          <ChevronRight className="size-4 text-fg-3" />
        )}
        <span className="font-medium text-fg">Set for Every File</span>
        <span className="min-w-0 flex-1 truncate text-sm text-fg-3">
          {presetLine ?? "Optional. The agent fills in anything you leave."}
        </span>
      </button>
      {presetsOpen ? (
        <div className="grid gap-3 border-t border-line-1 px-3 py-3">
          <div className="grid gap-1.5">
            <Label htmlFor="upload-preset-kind">What Are They?</Label>
            <Select
              value={presets.category ?? AUTO}
              onValueChange={(value) =>
                setPresets((p) => ({
                  ...p,
                  category:
                    value === AUTO ? null : (value as FirmLibraryCategory)
                }))
              }
            >
              <SelectTrigger id="upload-preset-kind" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTO}>Let Casey Decide</SelectItem>
                {CATEGORIES.map((category) => (
                  <SelectItem key={category} value={category}>
                    {CATEGORY_LABEL[category]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="upload-preset-visas">Visas</Label>
            <VisaPicker
              multiple
              id="upload-preset-visas"
              value={presets.visas ?? []}
              onChange={(visas) => setPresets((p) => ({ ...p, visas }))}
              placeholder="Any visa"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="upload-preset-tags">Tags</Label>
            <TagInput
              id="upload-preset-tags"
              value={presets.tags ?? []}
              onChange={(tags) => setPresets((p) => ({ ...p, tags }))}
              placeholder="Type a tag and press Enter"
            />
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <UploadDialogShell
      open={open}
      onRequestClose={close}
      title="Add Documents"
      description="Drop in filed briefs, letters and shells. The agent reads each one and writes its description and guidance; edit anything later."
      rail={rail}
      surface={
        <UploadSurface
          onFiles={addCollected}
          disabled={busy}
          hint={HINT}
          accept={ACCEPTED_EXTENSIONS}
          extraSources={
            <UploadSourceButton
              icon={<Briefcase />}
              label="From a Case"
              disabled={busy}
              onClick={() => setFromCaseOpen(true)}
              testId="upload-from-case"
            />
          }
        />
      }
      below={
        <>
          {destinationSection}
          {presetsSection}
        </>
      }
      preview={
        selected
          ? {
              id: selected.key,
              name: selected.file.name,
              onClose: () => setSelectedKey(null),
              body: (
                <StagedFilePreview
                  file={selected.file}
                  name={selected.file.name}
                  id={selected.key}
                />
              )
            }
          : null
      }
      summary={summary}
      onCancel={close}
      cancelDisabled={busy}
      action={{
        onClick: () => void submit(),
        disabled: sending.length === 0 || busy || checking,
        busy: busy || checking,
        testId: "upload-submit",
        label: busy
          ? "Uploading…"
          : sending.length
          ? `Add ${sending.length} to Library`
          : "Add to Library"
      }}
    >
      {fromCaseOpen ? (
        <AddFromCaseDialog
          open
          onOpenChange={setFromCaseOpen}
          onPick={(file) => addFiles([{ file, relativePath: file.name }])}
        />
      ) : null}
    </UploadDialogShell>
  );
};
